import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Logo } from '@/components/shared/Logo';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { Send, User, Mail, Phone, MessageSquare, Instagram, Youtube, ChevronDown } from 'lucide-react';

const ruqaaFont = { fontFamily: "'Aref Ruqaa', 'Amiri', serif" };
const arabicFont = { fontFamily: "'Almarai', 'Cairo', sans-serif" };

const countryCodes = [
  { code: '+249', country: 'السودان', iso: 'sd' },
  { code: '+966', country: 'السعودية', iso: 'sa' },
  { code: '+971', country: 'الإمارات', iso: 'ae' },
  { code: '+965', country: 'الكويت', iso: 'kw' },
  { code: '+974', country: 'قطر', iso: 'qa' },
  { code: '+973', country: 'البحرين', iso: 'bh' },
  { code: '+968', country: 'عُمان', iso: 'om' },
  { code: '+962', country: 'الأردن', iso: 'jo' },
  { code: '+20', country: 'مصر', iso: 'eg' },
  { code: '+212', country: 'المغرب', iso: 'ma' },
  { code: '+216', country: 'تونس', iso: 'tn' },
  { code: '+213', country: 'الجزائر', iso: 'dz' },
  { code: '+218', country: 'ليبيا', iso: 'ly' },
  { code: '+961', country: 'لبنان', iso: 'lb' },
  { code: '+963', country: 'سوريا', iso: 'sy' },
  { code: '+964', country: 'العراق', iso: 'iq' },
  { code: '+967', country: 'اليمن', iso: 'ye' },
  { code: '+970', country: 'فلسطين', iso: 'ps' },
];

// Flag image URL from flagcdn
const getFlagUrl = (iso: string) => `https://flagcdn.com/w40/${iso}.png`;

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+249',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  const selectedCountry = countryCodes.find(c => c.code === formData.countryCode);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('يرجى ملء جميع الحقول المطلوبة');
      return;
    }

    setIsSubmitting(true);
    try {
      // TODO: Implement API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      toast.success('تم إرسال رسالتك بنجاح! سنتواصل معك قريباً');
      setFormData({ name: '', email: '', countryCode: '+249', phone: '', message: '' });
    } catch {
      toast.error('حدث خطأ، يرجى المحاولة مرة أخرى');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col" style={arabicFont}>
      {/* Header */}
      <header className="bg-white shadow-sm py-4 sticky top-0 z-50">
        <div className="container mx-auto px-4 flex items-center justify-between">
          {/* Logo */}
          <Link to="/">
            <Logo size="lg" />
          </Link>
          
          {/* Navigation */}
          <nav className="flex items-center gap-6">
            <Link 
              to="/" 
              className="text-gray-700 hover:text-green-600 font-bold text-lg transition-colors"
            >
              العب
            </Link>
            <Link 
              to="/contact" 
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-bold text-lg transition-colors"
            >
              تواصل معنا
            </Link>
            <Link 
              to="/login" 
              className="text-gray-700 hover:text-green-600 font-bold text-lg transition-colors"
            >
              دخول
            </Link>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-8">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 w-full max-w-lg">
          <h1 
            className="text-4xl md:text-5xl text-center mb-8 text-green-600"
            style={{ ...ruqaaFont, fontWeight: 700 }}
          >
            اتصل بنا
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Input */}
            <div className="relative">
              <input
                type="text"
                placeholder="أدخل اسمك"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-4 text-lg border-2 rounded-xl outline-none transition-all text-right pr-12
                  border-gray-200 focus:border-green-500
                  bg-white"
                style={arabicFont}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                <User className="w-5 h-5" />
              </div>
            </div>

            {/* Email Input */}
            <div className="relative">
              <input
                type="email"
                placeholder="البريد الإلكتروني"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-4 text-lg border-2 rounded-xl outline-none transition-all text-right pr-12
                  border-gray-200 focus:border-green-500
                  bg-white"
                style={arabicFont}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                <Mail className="w-5 h-5" />
              </div>
            </div>

            {/* Country Code Dropdown */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                className="w-full px-4 py-4 text-lg border-2 rounded-xl outline-none transition-all text-right
                  border-gray-200 focus:border-green-500
                  bg-white flex items-center justify-between"
              >
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${showCountryDropdown ? 'rotate-180' : ''}`} />
                <span className="flex items-center gap-3">
                  <span className="text-gray-700">{selectedCountry?.country} ({selectedCountry?.code})</span>
                  <img 
                    src={getFlagUrl(selectedCountry?.iso || 'sd')} 
                    alt={selectedCountry?.country} 
                    className="w-8 h-6 object-cover rounded shadow-sm"
                  />
                </span>
              </button>
              
              {showCountryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg z-10 max-h-60 overflow-y-auto">
                  {countryCodes.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, countryCode: country.code });
                        setShowCountryDropdown(false);
                      }}
                      className="w-full px-4 py-3 text-right hover:bg-green-50 flex items-center justify-end gap-3 transition-colors border-b border-gray-100 last:border-0"
                    >
                      <span className="text-gray-700">{country.country} ({country.code})</span>
                      <img 
                        src={getFlagUrl(country.iso)} 
                        alt={country.country} 
                        className="w-8 h-6 object-cover rounded shadow-sm"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Phone Input */}
            <div className="relative">
              <input
                type="tel"
                placeholder="رقم التليفون"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-4 text-lg border-2 rounded-xl outline-none transition-all text-right pr-12
                  border-gray-200 focus:border-green-500
                  bg-white"
                style={arabicFont}
                dir="ltr"
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                <Phone className="w-5 h-5" />
              </div>
            </div>

            {/* Message Textarea */}
            <div className="relative">
              <textarea
                placeholder="رسالتك"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                className="w-full px-4 py-4 text-lg border-2 rounded-xl outline-none transition-all text-right pr-12
                  border-gray-200 focus:border-green-500
                  bg-white resize-none"
                style={arabicFont}
              />
              <div className="absolute right-4 top-4 text-gray-400">
                <MessageSquare className="w-5 h-5" />
              </div>
            </div>

            {/* Submit Button */}
            <button 
              type="submit" 
              className="w-full py-4 text-xl font-bold rounded-xl text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2
                bg-gradient-to-r from-green-600 to-green-500"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  إرسال
                </>
              )}
            </button>
          </form>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Social Media Icons */}
            <div className="flex items-center gap-4">
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                {/* TikTok */}
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/></svg>
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                <Youtube className="w-5 h-5" />
              </a>
              <a href="#" className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center hover:bg-white/20 transition-colors">
                {/* X (Twitter) */}
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              </a>
            </div>

            {/* Copyright */}
            <p className="text-gray-400 text-sm">
              Copyright 2026 اعصر مخك - All Rights Reserved ©
            </p>

            {/* App Store Links */}
            <div className="flex items-center gap-3">
              <a href="#" className="text-white/60 hover:text-white transition-colors">
                {/* Google Play */}
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>
              </a>
              <a href="#" className="text-white/60 hover:text-white transition-colors">
                {/* Apple */}
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContactPage;
